import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  FILTER_LEAVE_TYPE_KEY,
  FILTER_LOCATION_KEY,
} from "../../../assets/constants";
import LoadingButton from "../../../design/wilya-loading-button";
import { DataRelation } from "../../filters/filter-context";
import useTimeOffBalancesReportData
  from "../controllers/use-time-off-balances-report-data"; // Adjust path as needed
import { TimeOffBalance } from "../store/time-off-balances-report-types";

interface UpdateLeaveBalanceDialogProps {
  open: boolean;
  onClose: () => void;
  rowData: TimeOffBalance | null;
  allRows: TimeOffBalance[]; // Needed to lookup balances if user switches dropdown
  availableTimeOffTypes: { name: string, id: number }[]; // From filters
  onConfirm: (payload: {
    workerId: string;
    leaveTypeId: number;
    availableHours: number;
    workerInfo?: any;
  }) => void;
  isBadgeNumberEnabled: boolean;
  filterRelationships: DataRelation[];
  allLocations: { name: string, id: number }[];
}

const UpdateLeaveBalanceDialog = ({
                                    open,
                                    onClose,
                                    rowData,
                                    allRows,
                                    availableTimeOffTypes,
                                    onConfirm,
                                    isBadgeNumberEnabled,
                                    filterRelationships,
                                    allLocations
                                  }: UpdateLeaveBalanceDialogProps) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<number>(0);
  const [currentBalanceMinutes, setCurrentBalanceMinutes] = useState<number>(0);
  const [isNoLimitType, setIsNoLimitType] = useState<boolean>(false);
  const [changeHours, setChangeHours] = useState<string>("1");
  const [error, setError] = useState<string>("");
  
  const {
    updateBalanceStatus
  } = useTimeOffBalancesReportData();

  const workerAvailableTypes = React.useMemo(() => {
    if (!rowData || !rowData.location) return [];

    const locationId = allLocations.find((l) => l.name === rowData.location)?.id;
    if (!locationId) return [];

    const relationship = filterRelationships.find(
      (rel) => rel.to === FILTER_LEAVE_TYPE_KEY && rel.from === FILTER_LOCATION_KEY
    );

    if (!relationship) return [];

    const allowedTypeIds = relationship.mappings.find(
      (m) => m.fromId === locationId
    )?.toIds;

    if (!allowedTypeIds) return [];

    return availableTimeOffTypes.filter((type) => allowedTypeIds.includes(type.id));
  }, [rowData, allLocations, filterRelationships, availableTimeOffTypes]);

  // Initialize data when dialog opens
  useEffect(() => {
    if (open && rowData) {
      const typeId = rowData.timeOffTypeId ?? 0;
      setSelectedType(typeId);
      
      const foundRow = allRows.find(
        (r) =>
          r.workerId === rowData?.workerId &&
          r.timeOffTypeId === typeId
      );

      setCurrentBalanceMinutes(foundRow?.balanceMinutes || 0);
      setIsNoLimitType(foundRow?.isNoLimit || false);
      setChangeHours("1");
      setError("");
    }
  }, [open, rowData, allRows]);

  // Handle Dropdown Change
  const handleTypeChange = (event: SelectChangeEvent) => {
    const newType = Number(event.target.value);
    setSelectedType(newType);

    const foundRow = allRows.find(
      (r) =>
        r.workerId === rowData?.workerId &&
        r.timeOffTypeId === newType
    );
    setCurrentBalanceMinutes(foundRow?.balanceMinutes || 0);
    setIsNoLimitType(foundRow?.isNoLimit || false);
    setChangeHours("1"); // Reset change when switching types
  };

  // Helper to format minutes to "Xh Ym"
  const formatMinutesToTime = (totalMinutes: number) => {
    const isNegative = totalMinutes < 0;
    const absMinutes = Math.abs(totalMinutes);
    const h = Math.floor(absMinutes / 60);
    const m = Math.round(absMinutes % 60);
    return `${isNegative ? "-" : ""}${h}h ${m}m`;
  };

  // Calculate New Balance
  const getNewBalance = () => {
    const change = (changeHours === "" || changeHours === "-") ? 0 : parseFloat(changeHours);
    if (isNaN(change)) return currentBalanceMinutes;
    const changeInMinutes = Math.round(change * 60);
    const baseMinutes = Math.max(0, currentBalanceMinutes);
    return baseMinutes + changeInMinutes;
  };

  // Input Validation Logic
  // Input Validation Logic
  // Input Validation Logic
  const handleInputChange = (val: string) => {
    // 1. Block special characters (allow only digits, one decimal point, and leading minus)
    // This regex allows an optional minus sign, followed by digits and one optional decimal
    const regex = /^-?\d*\.?\d*$/;
    if (!regex.test(val)) return;

    // 2. Restrict to 2 digits max (excluding symbols like '-', '+', '.')
    // We strip non-digits to count how many actual numbers the user typed
    const digitCount = val.replace(/[^0-9]/g, "").length;
    if (digitCount > 2) return;

    // Prevent leading zeros unless it's just "0" or "0."
    if (val.length > 1 && val.startsWith("0") && val[1] !== ".") {
      return;
    }

    // Allow typing "-" or empty state for UX
    if (val === "" || val === "-") {
      setChangeHours(val);
      setError("");
      return;
    }

    const num = parseFloat(val);

    if (isNaN(num)) return;

    // Range validation
    if (num < -16 || num > 16) {
      setError(t("time-off-balances_value-range-error"));
    } else {
      setError("");
    }

    setChangeHours(val);
  };

  const adjustValue = (amount: number) => {
    let current = parseFloat(changeHours);
    if (isNaN(current)) current = 0;

    const nextChange = current + amount;
    const baseMinutes = Math.max(0, currentBalanceMinutes);
    const nextBalanceMinutes = baseMinutes + (nextChange * 60);
    const existingMinutes = rowData?.balanceMinutes ? Number(rowData?.balanceMinutes) || 0 : 0;
    if (nextChange >= -16 && nextChange <= 16 && nextBalanceMinutes >= 0) {
      setChangeHours(String(parseFloat(nextChange.toFixed(2))));
      setError("");
    }
  };

  const handleSave = () => {
    if(error || isNaN(parseFloat(changeHours))) return;
    const selectedTypeName = availableTimeOffTypes.find((t) => t.id === selectedType)?.name || "";

    const availableHours = Number((getNewBalance()/60).toFixed(2));
    if (rowData?.workerId)
    onConfirm({
      workerId: rowData?.workerId,
      leaveTypeId: selectedType,
      availableHours: availableHours,
      workerInfo: {
        companyEmployeeId: rowData.companyEmployeeId,
        fullName: rowData.fullName,
        badgeNumber: rowData.badgeNumber,
        jobTitle: rowData.jobTitle,
        workcenter: rowData.workcenter,
        location: rowData.location,
        shiftPattern: rowData.shiftPattern,
        reportingManager: rowData.reportingManager,
        timeOffType: selectedTypeName,
      }
    });
  };

  useEffect(() => {
    if(updateBalanceStatus === "fulfilled") {
      onClose();
    }
  }, [onClose, updateBalanceStatus]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", mb: 2 }}>
        {t("time-off-balances_update-leave-balance")}
      </DialogTitle>
      <DialogContent>
        {/* Worker Info Header */}
        <Box sx={{ mb: 0, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ display: "block" }}
              >
                {t("time-off-balances_worker-name")}
              </Typography>
              <Typography variant="body1" fontWeight="500" sx={{
                fontFamily: "Roboto",
                color: "rgba(0, 0, 0, 0.87)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {rowData?.fullName || "—"}
              </Typography>
            </Grid>
            {isBadgeNumberEnabled && rowData?.badgeNumber ? (
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ display: "block" }}
                >
                  {t("time-off-balances_badge-number")}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{
                  fontFamily: "Roboto",
                  color: "rgba(0, 0, 0, 0.87)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {rowData?.badgeNumber || "—"}
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ display: "block" }}
                >
                  {t("time-off-balances_worker-id")}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {rowData?.companyEmployeeId || "—"}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          {/* 1. Time Off Type Selection */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ mb: 1 }}
            >
              {t("time-off-balances_time-off-type")}
            </Typography>
            <FormControl fullWidth>
              <Select
                value={
                  workerAvailableTypes
                    .find((t) => t.id === selectedType)
                    ?.id.toString() ?? ""
                }
                onChange={handleTypeChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return t("time-off-balances_select-type");
                  return availableTimeOffTypes.find(
                    (t) => t.id === selectedType
                  )?.name;
                }}
              >
                {workerAvailableTypes.map((type) => {
                  // Logic to find specific balance for the dropdown item render
                  const typeRow = allRows.find(
                    (r) =>
                      r.workerId === rowData?.workerId &&
                      r.timeOffTypeId === type.id
                  );
                  const bal = typeRow?.isNoLimit ? t("time-off-balances_no-limit") : formatMinutesToTime(typeRow?.balanceMinutes || 0);

                  return (
                    <MenuItem
                      key={type.id}
                      value={type.id}
                      sx={{ display: "block", py: 1.5 }}
                    >
                      <Box>
                        <Typography variant="body1">{type.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {bal}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* 2. Balance Preview Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                bgcolor: "#f5f5f5",
                p: 2,
                borderRadius: 2,
              }}
            >
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  {t("time-off-balances_current-balance")}
                </Typography>
                <Typography variant="h6">
                  {isNoLimitType ? t("time-off-balances_no-limit") : formatMinutesToTime(currentBalanceMinutes)}
                </Typography>
                {currentBalanceMinutes < 0 && !isNoLimitType && (
                  <Typography variant="caption" color="error" sx={{
                    fontFamily: "Roboto",
                    display: "block",
                    fontSize: "10px" }}>
                    {t("time-off-balances_treated-as-zero")}
                  </Typography>
                )}
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary" sx={{
                  fontFamily: "Roboto",
                }}>
                  {t("time-off-balances_change")}
                </Typography>
                <Typography
                  variant="h6"
                  color={
                    parseFloat(changeHours) < 0 ? "error.main" : "primary.main"
                  }
                >
                  {!changeHours || changeHours === "-" ? (
                    "0h"
                  ) : (
                    <>
                      {parseFloat(changeHours) > 0 ? "+" : ""}
                      {changeHours}h
                    </>
                  )}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  {t("time-off-balances_new-balance")}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {isNoLimitType ? t("time-off-balances_no-limit") : formatMinutesToTime(getNewBalance())}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* 3. Change Input Field */}
          <Grid item xs={12}>
            <TextField
              label={t("time-off-balances_change-hours")}
              fullWidth
              value={changeHours}
              onChange={(e) => handleInputChange(e.target.value)}
              disabled={isNoLimitType}
              error={!!error}
              helperText={
                isNoLimitType ? t("time-off-balances_no-limit-helper") : (
                error ||
                t("time-off-balances_change-helper")
                )
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small" onClick={() => adjustValue(-1)} disabled={isNoLimitType}>
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => adjustValue(1)} disabled={isNoLimitType}>
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: {
                  style: { textAlign: "center" },
                },
              }}
              type="text"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            fontFamily: "Roboto",
            fontSize: "14px",
          }}
        >
          {t("common_cancel")}
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          disabled={
            isNoLimitType ||
            !!error ||
            changeHours === "" ||
            changeHours === "-" ||
            updateBalanceStatus !== "idle" ||
            getNewBalance() === currentBalanceMinutes
          }
          loading={updateBalanceStatus === "pending"}
          loadingIndicator={
            <CircularProgress size={16} sx={{ color: "rgba(0, 0, 0, 0.38)" }} />
          }
          loadingPosition="start"
        >
          {updateBalanceStatus === "pending" ? t("time-off-balances_updating") : t("time-off-balances_update")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateLeaveBalanceDialog;